/**
 * TicketBadge Component
 *
 * Displays a linked Jira ticket badge that replaces the Jira icon
 * after a ticket has been created from a comment.
 *
 * Features:
 * - Shows Jira icon + ticket key (e.g., "PROJ-123")
 * - Clickable - opens ticket in Jira (new tab)
 * - Hover state with darker background
 * - Tooltip "Open in Jira"
 * - Uses PDS design tokens
 */

import React, { useState } from 'react';
import type { JiraTicket } from '../../lib/types';
import { tokens } from '../../styles/tokens';
import { JiraIcon, ExternalLinkIcon } from '../icons/Icons';

export interface TicketBadgeProps {
  /** The linked Jira ticket */
  ticket: JiraTicket;
  /** Optional callback when clicked (defaults to opening URL in new tab) */
  onClick?: (ticket: JiraTicket) => void;
}

export const TicketBadge: React.FC<TicketBadgeProps> = ({ ticket, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick(ticket);
    } else {
      window.open(ticket.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title="Open in Jira"
      aria-label={`Open Jira ticket ${ticket.key}`}
      data-testid={`ticket-badge-${ticket.key}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacing['5xs'],
        padding: `${tokens.spacing['5xs']} ${tokens.spacing['2xs']}`,
        backgroundColor: isHovered ? tokens.colors.primaryBorder : tokens.colors.primaryLight,
        border: `${tokens.borders.widthDefault} solid ${tokens.colors.primaryBorder}`,
        borderRadius: tokens.borders.radiusButton,
        cursor: 'pointer',
        transition: `background-color ${tokens.animation.duration} ${tokens.animation.timing}`,
      }}
    >
      <JiraIcon size={12} color="#4f32ce" />
      <span
        style={{
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: tokens.typography.fontWeight.medium,
          color: tokens.colors.primary,
          lineHeight: 1,
        }}
      >
        {ticket.key}
      </span>
      {isHovered && <ExternalLinkIcon size={10} color="#4f32ce" />}
    </button>
  );
};

export default TicketBadge;
